import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import * as React from 'react'
import { requireCapability } from '@/lib/auth'
import { api,type AxiosError,type MaybeEnveloped,type ListEnvelope } from '@/lib/api-client'
import { createFileRoute,Outlet,useNavigate,useSearch } from '@tanstack/react-router'
import { z } from 'zod'
import { useQuery,useMutation,useQueryClient,type QueryKey } from '@tanstack/react-query'
import { toast } from 'sonner'
import { formatDistanceToNow } from 'date-fns'
import {
Table,
TableBody,
TableCell,
TableHead,
TableHeader,
TableRow,
} from '@/components/ui/table'
import { Card,CardContent,CardDescription,CardHeader,CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
X,
Plus,
MoreHorizontal,
MoreVertical,
Pencil,
Trash2,Clock,
Search,
RefreshCw
} from 'lucide-react'
import {
DropdownMenu,
DropdownMenuContent,
DropdownMenuItem,
DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PageBreadcrumb } from '@/components/dashboard/page-breadcrumb'
import { Skeleton } from '@/components/ui/skeleton'
import { DataTablePagination } from '@/components/dashboard/data-table-pagination'
import { queryKeys } from '@/lib/query-keys'
import { extractArray } from '@/features/http'
import { useSelectedShopId } from '@/features/shared'
import { DeleteConfirmationDialog } from '@/components/delete-confirmation-dialog'
import { FormDialog } from '@/components/form-dialog'
import { formatInStoreTz } from '@/lib/time'

interface TagItem {
  id: string
  name: string
  slug: string
  colorCode?: string
  createdAt?: string
  updatedAt?: string
}

type PaginatedData<T> = {
  data: T[]
  meta: {
    page: number
    totalPages: number
    totalItems: number
  }
}

type OptimisticPaginatedContext<T> = {
  previousData: [QueryKey, PaginatedData<T> | undefined][]
}

const searchSchema = z.object({
  page: z.number().catch(1),
  limit: z.number().catch(10),
  search: z.string().catch(''),
})

export const Route = createFileRoute('/_dashboard/catalog/tags')({
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  beforeLoad: requireCapability('MANAGE_CATALOG'),
  validateSearch: searchSchema,
  component: TagsLayout,
})

let suppressedTagsAutoOpenSignature: string | null = null

function TagsLayout() {
  return <Outlet />
}

export function TagsPage({ selectedId }: { selectedId: string | undefined }) {
  const navigate = useNavigate()
  const { page, limit, search: searchQuery } = useSearch({ from: Route.id })
  const shopId = useSelectedShopId()
  const pageSignature = `${shopId ?? 'no-shop'}:${page}:${limit}:${searchQuery}`
  const queryClient = useQueryClient()

  const [searchInput, setSearchInput] = React.useState(searchQuery)
  const [isRefreshing, setIsRefreshing] = React.useState(false)
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false)
  const [isEditing, setIsEditing] = React.useState(false)
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [tagToDelete, setTagToDelete] = React.useState<TagItem | null>(null)

  const [name, setName] = React.useState('')
  const [colorCode, setColorCode] = React.useState('#000000')
  const [errors, setErrors] = React.useState<{ name?: string }>({})

  React.useEffect(() => {
    const timer = setTimeout(() => {
      // Only a changed term resets to page 1 — on mount the term is unchanged,
      // so reloads and shared links keep their ?page=.
      navigate({ to: '.', search: (prev) => ((prev.search ?? '') === searchInput ? prev : { ...prev, search: searchInput, page: 1 }), replace: true })
    }, 500)
    return () => clearTimeout(timer)
  }, [searchInput, navigate])

  const setSelectedId = React.useCallback(
    (id: string | null) => {
      if (id) {
        navigate({
          to: '/catalog/tags/$item',
          params: { item: id },
          search: (prev) => ({ ...prev }),
        })
        return
      }

        navigate({ to: '/catalog/tags', search: (prev) => ({ ...prev }) })
    },
    [navigate]
  )

  const tagsQuery = useQuery({
    queryKey: [...queryKeys.tags(shopId), { page, limit, search: searchQuery }],
    queryFn: async () => {
      const response = await api.get<ListEnvelope>(`/tags`, {
        params: { page, limit, search: searchQuery || undefined },
      })
      const data = extractArray<TagItem>(response)
      // The API returns `meta.total`, not `totalItems` — normalize it so the
      // "of N" count and the optimistic add/delete updates read the real total.
      const meta = {
        page: response.meta?.page ?? page,
        totalPages: response.meta?.totalPages ?? 1,
        totalItems: response.meta?.total ?? response.meta?.totalItems ?? data.length,
      }
      return { data, meta }
    },
    enabled: !!shopId,
  })

  const selectedTagQuery = useQuery({
    queryKey: queryKeys.tag(selectedId),
    queryFn: async () => {
      if (!selectedId) return null
      const response = await api.get<MaybeEnveloped<TagItem>>(`/tags/${selectedId}`)
      return response?.data || response
    },
    enabled: !!selectedId,
  })

  const tags = tagsQuery.data?.data || []
  const currentPageHasSelectedTag = !!selectedId && tags.some((tag) => tag.slug === selectedId || tag.id === selectedId)
  const effectiveSelectedId = (() => {
    if (currentPageHasSelectedTag) return selectedId
    if (tagsQuery.isLoading || tags.length === 0) return undefined
    if (!selectedId && suppressedTagsAutoOpenSignature === pageSignature) return undefined
    return tags[0].slug || tags[0].id
  })()
  const selectedTag = tags.find((t) => t.slug === effectiveSelectedId || t.id === effectiveSelectedId) || selectedTagQuery.data || null
  const totalItems = tagsQuery.data?.meta?.totalItems || tags.length
  const totalPages = tagsQuery.data?.meta?.totalPages || 1

  const closeSelectedTag = React.useCallback(() => {
    suppressedTagsAutoOpenSignature = pageSignature
    setSelectedId(null)
  }, [pageSignature, setSelectedId])

  React.useEffect(() => {
    if (!effectiveSelectedId || effectiveSelectedId === selectedId) return
    setSelectedId(effectiveSelectedId)
  }, [effectiveSelectedId, selectedId, setSelectedId])

  React.useEffect(() => {
    return () => {
      window.setTimeout(() => {
        if (!window.location.pathname.startsWith('/catalog/tags')) {
          suppressedTagsAutoOpenSignature = null
        }
      }, 0)
    }
  }, [])

  const saveTag = useMutation<void, unknown, Partial<TagItem> & { id?: string }, OptimisticPaginatedContext<TagItem>>({
    mutationFn: async (payload) => {
      if (payload.id) {
        const { id, ...data } = payload
        await api.patch(`/tags/${id}`, data)
      } else {
        await api.post(`/tags`, payload)
      }
    },
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.tags(shopId) })
      const previousData = queryClient.getQueriesData<PaginatedData<TagItem>>({ queryKey: queryKeys.tags(shopId) })

      queryClient.setQueriesData<PaginatedData<TagItem>>(
        { queryKey: queryKeys.tags(shopId) },
        (old) => {
          if (!old) return old

          if (payload.id) {
            return {
              ...old,
              data: old.data.map((item) =>
                item.id === payload.id ? { ...item, ...payload } : item
              ),
            }
          }

          const tempTag: TagItem = {
            id: `temp-${Date.now()}`,
            name: payload.name || '',
            slug: payload.name || '',
            ...payload,
          }

          return {
            ...old,
            data: old.meta.page === 1 ? [tempTag, ...old.data] : old.data,
            meta: { ...old.meta, totalItems: old.meta.totalItems + 1 },
          }
        }
      )

      return { previousData }
    },
    onSuccess: (_, payload) => {
      toast.success(`Tag ${payload.id ? 'updated' : 'created'} successfully`)
      handleCloseDialog(false)
    },
    onError: (error: unknown, _payload, context) => {
      if (context?.previousData) {
        for (const [key, data] of context.previousData) {
          queryClient.setQueryData(key, data)
        }
      }
      toast.error((error as AxiosError<{ message?: string }>).response?.data?.message || 'Failed to save tag')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tags(shopId) })
    },
  })

  const deleteTag = useMutation<void, unknown, string, OptimisticPaginatedContext<TagItem>>({
    mutationFn: async (id: string) => {
      await api.delete(`/tags/${id}`)
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.tags(shopId) })
      const previousData = queryClient.getQueriesData<PaginatedData<TagItem>>({ queryKey: queryKeys.tags(shopId) })

      queryClient.setQueriesData<PaginatedData<TagItem>>(
        { queryKey: queryKeys.tags(shopId) },
        (old) => {
          if (!old) return old
          return {
            ...old,
            data: old.data.filter((item) => item.id !== id),
            meta: { ...old.meta, totalItems: Math.max(0, old.meta.totalItems - 1) },
          }
        }
      )

      return { previousData }
    },
    onSuccess: (_, deletedId) => {
      if (selectedId === deletedId) setSelectedId(null)
      toast.success('Tag deleted successfully')
    },
    onError: (error: unknown, _id, context) => {
      if (context?.previousData) {
        for (const [key, data] of context.previousData) {
          queryClient.setQueryData(key, data)
        }
      }
      toast.error('Failed to delete tag', { description: (error as AxiosError<{ message?: string }>).response?.data?.message })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tags(shopId) })
    },
  })

  const handleCloseDialog = (open: boolean) => {
    setIsAddDialogOpen(open)
    if (!open) {
      setTimeout(() => {
        setName('')
        setColorCode('#000000')
        setErrors({})
        setIsEditing(false)
        setEditingId(null)
      }, 300)
    }
  }

  const handleEdit = (item: TagItem) => {
    setName(item.name)
    setColorCode(item.colorCode || '#000000')
    setEditingId(item.id)
    setIsEditing(true)
    setIsAddDialogOpen(true)
  }

  const handleSave = () => {
    if (!name.trim()) {
      setErrors({ name: 'Tag name is required' })
      return
    }
    const payload: Partial<TagItem> & { id?: string } = { name, colorCode }
    if (isEditing && editingId) {
      payload.id = editingId
    }
    saveTag.mutate(payload)
  }

  return (
    <div className="space-y-6">
      <PageBreadcrumb />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Tags</h2>
          <p className="text-muted-foreground">Manage product tags and labels.</p>
        </div>
        {(tags.length > 0 || tagsQuery.isLoading) && (
          <Button
            onClick={() => {
              setIsEditing(false)
              setIsAddDialogOpen(true)
            }}
            className="transition-all hover:scale-105 hover:shadow-md"
          >
            <Plus className="mr-0.5 h-4 w-4" /> Add Tag
          </Button>
        )}

        <FormDialog
          open={isAddDialogOpen}
          onOpenChange={handleCloseDialog}
          title={isEditing ? 'Edit Tag' : 'Add Tag'}
          description={
            isEditing
              ? 'Update existing tag details.'
              : "Create a new tag here. Click save when you're done."
          }
          onConfirm={handleSave}
          confirmLabel={isEditing ? 'Update Tag' : 'Save Tag'}
          isPending={saveTag.isPending}
          isConfirmDisabled={!name.trim()}
          size="slim"
        >
          <div className="grid gap-4 py-1">
            <div className="grid gap-2">
              <Label htmlFor="name" className="gap-0.5">
                Name<span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                placeholder="e.g. Organic"
                required
                value={name}
                onChange={(e) => {
                  setName(e.target.value)
                  if (errors.name)
                    setErrors((prev) => ({ ...prev, name: undefined }))
                }}
                className={
                  errors.name
                    ? 'border-destructive focus-visible:ring-destructive'
                    : ''
                }
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name}</p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="colorCode">Color</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="colorCode"
                  type="color"
                  value={colorCode}
                  onChange={(e) => setColorCode(e.target.value)}
                  className="w-12 h-10 p-1 cursor-pointer overflow-hidden border-none bg-transparent"
                />
                <Input
                  value={colorCode}
                  onChange={(e) => setColorCode(e.target.value)}
                  placeholder="#000000"
                  className="flex-1 font-mono uppercase"
                  maxLength={7}
                />
              </div>
            </div>
          </div>
        </FormDialog>
      </div>

      {(tags.length > 0 || searchQuery || tagsQuery.isLoading) && (
        <div className="flex items-center gap-2 max-w-sm w-full">
          <div className="relative flex-1 group">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search tags..."
              className="pl-9 pr-8"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSearchInput('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Clear search</span>
              </Button>
            )}
          </div>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            title="Refresh"
            disabled={isRefreshing}
            onClick={() => {
              setIsRefreshing(true)
              void tagsQuery.refetch()
              setTimeout(() => setIsRefreshing(false), 600)
            }}
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div className={`flex-1 min-w-0 transition-all duration-300 ${!tagsQuery.isLoading && tags.length === 0 ? '' : 'rounded-md squircle border overflow-hidden'} ${selectedTag ? 'w-full md:w-[calc(100%-340px)] lg:w-[calc(100%-380px)]' : 'w-full'}`}>
          {!tagsQuery.isLoading && tags.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 gap-6 text-center">
              <div className="relative w-24 h-24 opacity-10 grayscale shrink-0">
                <img src="/misc/logo.png" alt="Rootpoint Logo" className="w-full h-full object-contain" />
              </div>
              <div className="flex flex-col gap-4 items-center">
                <p className="font-medium text-muted-foreground text-lg">No tags found</p>
                <Button
                  onClick={() => {
                    setIsEditing(false)
                    setIsAddDialogOpen(true)
                  }}
                  className="w-fit"
                >
                  <Plus className="mr-2 h-4 w-4" /> Add Tag
                </Button>
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead>Tag</TableHead>
                  <TableHead className="w-[80px] text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {tagsQuery.isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                      <TableCell><Skeleton className="h-8 w-8 ml-auto" /></TableCell>
                    </TableRow>
                  ))
                ) : (
                  tags.map((item) => (
                    <TableRow
                      key={item.id}
                      className={`cursor-pointer transition-colors group ${effectiveSelectedId === item.id || effectiveSelectedId === item.slug ? 'bg-muted' : ''}`}
                      onClick={() => {
                        if (item.slug === effectiveSelectedId || item.id === effectiveSelectedId) {
                          closeSelectedTag()
                          return
                        }
                        setSelectedId(item.slug || item.id)
                      }}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-1.5">
                          <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: item.colorCode || '#eee' }}></div>
                          {item.name}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0 opacity-100 xl:opacity-0 xl:group-hover:opacity-100 transition-opacity">
                              <span className="sr-only">Open menu</span>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-32">
                            <DropdownMenuItem className="gap-1.5" onClick={(e) => { e.stopPropagation(); handleEdit(item); }}>
                              <Pencil className="h-4 w-4" />Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem className="gap-1.5 text-destructive focus:text-destructive" onClick={(e) => { e.stopPropagation(); setTagToDelete(item); }}>
                              <Trash2 className="h-4 w-4" />Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}

          <DataTablePagination
            page={page}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={limit}
            entityName="tags"
            isLoading={tagsQuery.isLoading}
            onPageChange={(p: number) => navigate({ to: '.', search: (prev) => ({ ...prev, page: p }), replace: true })}
            onLimitChange={(l: number) => navigate({ to: '.', search: (prev) => ({ ...prev, limit: l, page: 1 }), replace: true })}
          />
        </div>

        {selectedTag && (
          <Card className="squircle mx-0.5 mb-0.5 w-full md:w-[320px] lg:w-[360px] sticky top-0 flex flex-col h-fit max-h-[calc(100vh-6.5rem)] overflow-hidden animate-in slide-in-from-right-10 fade-in duration-300 shadow-lg pb-4">
            <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2 bg-card z-10">
              <div className="grid gap-0">
                <CardTitle className="text-xl flex items-center gap-1.5 leading-tight">{selectedTag.name}</CardTitle>
                <CardDescription className="font-mono text-xs -mt-0.5">{selectedTag.slug}</CardDescription>
              </div>
              <div className="flex items-center gap-1 -mt-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="hover:scale-105 transition-transform duration-200"
                    >
                      <MoreVertical className="h-4 w-4" />
                      <span className="sr-only">Open menu</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-32">
                    <DropdownMenuItem className="gap-1.5" onClick={() => handleEdit(selectedTag)}>
                      <Pencil className="h-4 w-4" />Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="gap-1.5 text-destructive focus:text-destructive"
                      onClick={() => setTagToDelete(selectedTag)}
                    >
                      <Trash2 className="h-4 w-4" />Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={closeSelectedTag}
                  className="transition-transform duration-200"
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="grid gap-4 overflow-y-auto pb-0 px-6">
              <div className="space-y-4">
                <div className="space-y-1">
                  <h4 className="font-medium leading-none text-muted-foreground text-xs uppercase tracking-wider">Color Code</h4>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: selectedTag.colorCode || '#000000' }} />
                    <code className="text-xs bg-muted p-1 rounded font-mono">{selectedTag.colorCode || 'N/A'}</code>
                  </div>
                </div>
              </div>

              <div className="pt-2 mt-2 border-t border-border/50 grid grid-cols-2 gap-4 text-xs text-muted-foreground">
                <div>
                  <span className="font-medium text-foreground block mb-1">Created</span>
                  {selectedTag.createdAt ? formatInStoreTz(selectedTag.createdAt, 'MMM d, yyyy') : 'N/A'}
                </div>
                <div>
                  <span className="font-medium text-foreground block mb-1">Updated</span>
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    {selectedTag.updatedAt ? formatDistanceToNow(new Date(selectedTag.updatedAt), { addSuffix: true }) : 'N/A'}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      <DeleteConfirmationDialog
        open={!!tagToDelete}
        onOpenChange={(open: boolean) => !open && setTagToDelete(null)}
        title="Delete Tag?"
        description={
          <>
            This action cannot be undone. This will permanently delete the tag{' '}
            <span className="font-medium text-foreground">"{tagToDelete?.name}"</span> and
            remove it from our servers.
          </>
        }
        onConfirm={() => {
          if (tagToDelete) {
            deleteTag.mutate(tagToDelete.id)
            setTagToDelete(null)
          }
        }}
        confirmLabel="Delete Tag"
        isPending={deleteTag.isPending}
      />
    </div>
  )
}
